import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { TextField, Button, Box, Typography, Link, CircularProgress, Alert } from '@mui/material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [fromPayment, setFromPayment] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const { login, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loadingTimerRef = useRef(null);

  // Check if we're coming from a payment
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromPaymentParam = params.get('from_payment');
    const sessionIdParam = params.get('session_id');

    if (fromPaymentParam === 'true') {
      setFromPayment(true);
      console.log('User is coming from payment page');
    }

    if (sessionIdParam) {
      setSessionId(sessionIdParam);
      localStorage.setItem('stripeSessionId', sessionIdParam);
      console.log('Stored session ID in localStorage:', sessionIdParam);
    }
  }, [location]);

  // Monitor loading state for timeouts
  useEffect(() => {
    if (loading) {
      // Set a timer to check if loading gets stuck
      loadingTimerRef.current = setTimeout(() => {
        console.log('Loading state timeout reached, redirecting to connection fix page');
        navigate('/fix-connection');
      }, 10000); // 10 seconds timeout
    } else {
      // Clear the timer if loading state changes to false
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Attempting login with:', { email });
      const result = await login(email, password);
      console.log('Login successful:', result);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      // Error is handled by AuthContext and displayed through authError
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 400,
        mx: 'auto',
        mt: 4,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom align="center">
        Log In
      </Typography>

      {fromPayment && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your payment was successful! Please log in or create an account to continue.
        </Alert>
      )}

      {authError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {authError}
        </Alert>
      )}

      <TextField
        type="email"
        label="Email"
        fullWidth
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
        disabled={loading}
        inputProps={{
          'aria-label': 'Email address'
        }}
      />

      <TextField
        type="password"
        label="Password"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        autoComplete="current-password"
        disabled={loading}
        inputProps={{
          'aria-label': 'Password'
        }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ mt: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Log In'}
      </Button>

      {fromPayment && (
        <Box mt={2} textAlign="center">
          <Typography variant="body2" gutterBottom>
            Don't have an account yet?
          </Typography>
          <Button
            component={RouterLink}
            to={`/signup${sessionId ? `?session_id=${sessionId}` : ''}`}
            variant="outlined"
            fullWidth
            sx={{ mt: 1 }}
          >
            Create an Account
          </Button>
        </Box>
      )}

      {/* Restored signup link */}
      <Box mt={2} textAlign="center">
        <Link
          component={RouterLink}
          to="/signup"
          variant="body2"
          underline="hover"
        >
          Don't have an account? Sign up
        </Link>
      </Box>

      {loading && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading may take a few moments...
          </Typography>
          <Button
            variant="text"
            color="secondary"
            onClick={() => navigate('/fix-connection')}
            sx={{ mt: 1 }}
          >
            Having trouble? Click here
          </Button>
        </Box>
      )}
    </Box>
  );
}
